class DossierPerfumeAssistant {
  constructor() {
    // Configuration || Settings
    this.apiBaseUrl = 'https://assistant.dossier.co';
    this.region = 'ca';
    this.chatIndex = 0;
    this.email = '';
    this.chatStarted = false;
    this.productHandles = [];
    this.emailStorageKey = 'dossierAssistantEmail';
    this.transcriptStorageKey = 'dossierAssistantTranscript';
    this.activeStreamController = null;
    this.pendingMessage = null;

    // Session tracking (assistant_events)
    this.trackUrl = `${this.apiBaseUrl}/conversations/track`;
    this.deviceIdStorageKey = 'dsr_device_id';
    this.trackingSessionStorageKey = 'dsr_assistant_session';
    this.trackingSessionGapMinutes = 30;

    // Classes || IDs
    this.headerMobileHideClass = 'no-header--mobile';
    this.perfumeAssistantClass = 'perfume-assistant';
    this.perfumeAssistantFormClass = 'perfume-assistant__form';
    this.buttonToggleRecommendedClass = 'button__toggle-recommended';

    // Elements || Selectors
    this.perfumeAssistantHeader = document.querySelector('.perfume-assistant__header');
    this.dossierPerfumeAssistant = document.querySelector('dossier-perfume-assistant');
    this.introStep = document.querySelector('.perfume-assistant__intro');
    this.introForm = this.introStep.querySelector(`.${this.perfumeAssistantFormClass}`);
    this.searchStep = document.querySelector('.perfume-assistant__search');
    this.searchForm = this.searchStep.querySelector(`.${this.perfumeAssistantFormClass}`);
    this.suggestionTextItems = this.searchStep.querySelectorAll('.perfume-assistant__suggestion');
    this.chatStep = document.querySelector('.perfume-assistant__chatAI');
    this.chatForm = this.chatStep.querySelector(`.${this.perfumeAssistantFormClass}`);
    this.chatbox = this.chatStep.querySelector('.perfume-assistant__chatbox');
    this.chatTemplate = document.getElementById('chat-template');
    this.productsContainer = document.querySelector('.perfume-assistant__products');
    this.perfumeSuggestions = document.querySelectorAll('.perfume-assistant__suggestions');
    this.suggestionsSlideshowContent = document.querySelector('.perfume-assistant__suggestions-content');
    this.suggestionsSlideshowNav = document.querySelector('.perfume-assistant__suggestions-nav');
    this.prevStepButton = document.querySelector('.step-action .button__prev-step');
    this.aiResults = document.querySelector('.perfume-assistant__ai-results');
    this.productsRecommended = document.querySelector('#products-recommended');

    // Ainee Popup
    this.aineePopup = document.getElementById('ainee-popup');

    //memory
    this.viewKey;
    this.productHandlesWithScentFamily = [];
  }

  init() {
    this.resolveStoredEmail();
    this.trackAssistantOpen();
    this.setPerfumeAssistantStep();
    this.restoreTranscript();
    this.loadSuggestedQueries();
    this.onEmailFormSubmit();
    this.onMessageFormSubmit();
    this.initSuggestionsSlideshow();
    this.updateContentOnScroll();
    this.navigateBack();
    this.handleSuggestionSelection();
    this.toggleRecommendedProducts();
    this.initMobileUpdate();
    this.closeAineePopup();
  }

  // Logged-in customers (Liquid) and returning guests (localStorage) skip the email gate
  resolveStoredEmail() {
    let storedEmail = '';
    try {
      storedEmail = localStorage.getItem(this.emailStorageKey) || '';
    } catch (error) {
      // localStorage unavailable — fall back to asking for the email
    }
    this.email = window.assistantCustomerEmail || storedEmail;
  }

  // --- Assistant session tracking (fire-and-forget; must never break the page) ---

  getDeviceId() {
    try {
      let id = localStorage.getItem(this.deviceIdStorageKey);
      if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem(this.deviceIdStorageKey, id);
      }
      return id;
    } catch (error) {
      return null; // storage blocked — still track, just without retention linkage
    }
  }

  // { id, isNew } — reuses the session while the gap since the last tracked
  // activity is under 30 min (mirrors how the backend sessionizes chats),
  // refreshing the activity timestamp on every call
  getTrackingSession() {
    try {
      const now = Date.now();
      const current = JSON.parse(sessionStorage.getItem(this.trackingSessionStorageKey) || 'null');
      if (current && now - current.last < this.trackingSessionGapMinutes * 60e3) {
        sessionStorage.setItem(this.trackingSessionStorageKey, JSON.stringify({ id: current.id, last: now }));
        return { id: current.id, isNew: false };
      }
      const id = crypto.randomUUID();
      sessionStorage.setItem(this.trackingSessionStorageKey, JSON.stringify({ id, last: now }));
      return { id, isNew: true };
    } catch (error) {
      return { id: null, isNew: true };
    }
  }

  track(event) {
    try {
      // string body → text/plain → no CORS preflight; the server parses it as
      // JSON and always answers 204. Do NOT set Content-Type: application/json.
      const body = JSON.stringify(event);
      if (navigator.sendBeacon && navigator.sendBeacon(this.trackUrl, body)) return;
      fetch(this.trackUrl, { method: 'POST', body, keepalive: true }).catch(() => {});
    } catch (error) {
      // analytics must never break the page
    }
  }

  // Exactly one open event per assistant session (sessions, not pageviews):
  // a reload within the 30 min window must not fire a second open
  trackAssistantOpen() {
    if (typeof window === 'undefined') return;
    const session = this.getTrackingSession();
    if (!session.isNew) return;
    this.track({
      type: 'open',
      region: this.region,
      sessionId: session.id,
      deviceId: this.getDeviceId(),
      email: this.email || null,
      page: location.pathname,
      referrer: document.referrer || null,
    });
  }

  // --- END Assistant session tracking ---

  // Everyone lands on the search step; guests get the email gate (step 1)
  // after their first message, before the first assistant reply
  setPerfumeAssistantStep() {
    document.body.dataset.perfumeAssistantStep = 2;
  }

  updatePerfumeAssistantStep(stepNumber) {
    const newStep = Math.max(1, Math.min(3, stepNumber));
    document.body.dataset.perfumeAssistantStep = newStep.toString();
  }

  onEmailFormSubmit() {
    const validateEmail = (email) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const form = this.introForm;
    const input = form.querySelector('#email');

    const handleEmailSubmit = async () => {
      const inputValue = input.value.trim();

      if (!validateEmail(inputValue)) {
        form.classList.add('error');
        return;
      }

      this.email = inputValue;

      try {
        const response = await fetch(`${this.apiBaseUrl}/users/ca/add/v2`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.email }),
        });

        if (!response.ok) {
          console.error('Failed to submit email:', response.statusText);
          return;
        }

        this.chatStarted = true;

        try {
          localStorage.setItem(this.emailStorageKey, this.email);
        } catch (error) {
          // localStorage unavailable — the email still works for this page view
        }

        input.value = '';
        form.classList.remove('error');

        // Send the message the guest typed before the email gate
        if (this.pendingMessage) {
          const pendingMessage = this.pendingMessage;
          this.pendingMessage = null;
          this.sendMessage(pendingMessage, true);
        } else {
          this.updatePerfumeAssistantStep(2);
        }
      } catch (error) {
        console.error('Error submitting email:', error);
      }
    };

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleEmailSubmit();
      }
    });

    input.addEventListener('input', () => {
      form.classList.remove('error');
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      handleEmailSubmit();
    });
  }

  onMessageFormSubmit() {
    const forms = [this.searchForm, this.chatForm];

    const handleSubmittedMessage = async (form) => {
      if (document.body.classList.contains('loading-ai-message')) return;

      const input = form.querySelector('#query');
      const userMessage = input.value.replace(/(\S)/, (m) => m.toUpperCase());

      if (userMessage === '') {
        form.classList.add('error');
        return;
      }

      input.value = '';
      form.classList.remove('error');

      // Email is required before the first assistant reply: hold the message
      // and show the email gate; it's sent once the email is submitted
      if (!this.email) {
        this.pendingMessage = userMessage;
        this.updatePerfumeAssistantStep(1);
        return;
      }

      await this.sendMessage(userMessage, form === this.searchForm);
    };

    // Attach event listeners for both forms
    forms.forEach((form) => {
      const input = form.querySelector('#query');

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          handleSubmittedMessage(form);
        }
      });

      input.addEventListener('input', () => {
        form.classList.remove('error');
      });

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        handleSubmittedMessage(form);
      });
    });
  }

  async sendMessage(userMessage, fromSearchStep) {
    // 📈 Extends the tracked session and feeds the opens→chats funnel
    this.track({
      type: 'message',
      region: this.region,
      sessionId: this.getTrackingSession().id,
      deviceId: this.getDeviceId(),
      email: this.email || null,
    });

    // Handle step transition if it's on the search step
    if (fromSearchStep && this.searchStep && this.chatStep) {
      this.updatePerfumeAssistantStep(3);
    }

    // 👤 Render the user message
    this.renderChatTemplate(userMessage);
    const currentChatEl = this.chatbox.querySelector(`.perfume-assistant__chat[data-key="${this.chatIndex - 1}"]`);

    if (!fromSearchStep && this.searchStep && this.chatStep) {
      window.scrollTo(0, document.body.scrollHeight);
    }

    this.activeStreamController = new AbortController();
    const signal = this.activeStreamController.signal;

    try {
      // Ensure the user exists server-side once per page load (safe to call repeatedly)
      if (this.chatStarted == false) {
        const userResponse = await fetch(`${this.apiBaseUrl}/users/ca/add/v2`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.email }),
          signal,
        });

        if (!userResponse.ok) {
          throw new Error(`Failed to submit email: ${userResponse.statusText}`);
        }

        this.chatStarted = true;
      }

      const messageResponse = await fetch(`${this.apiBaseUrl}/conversations/ca/add-message/v2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: this.email, message: userMessage }),
        signal,
      });

      if (!messageResponse.ok) {
        throw new Error(`Failed to submit message: ${messageResponse.statusText}`);
      }

      // 🤖 Stream the assistant reply: plain text while it streams,
      // swapped for rendered markdown once the done event arrives
      let streamEl = null;
      const showPartial = (partialText) => {
        if (!streamEl) {
          const wrapper = currentChatEl.querySelector('.ai-message-wrapper');
          const loadingEl = wrapper.querySelector('.loading-element');
          if (loadingEl) loadingEl.remove();
          streamEl = document.createElement('div');
          streamEl.classList.add('ai-message');
          wrapper.appendChild(streamEl);
        }
        streamEl.textContent = partialText;
        window.scrollTo(0, document.body.scrollHeight);
      };

      const rawReply = await this.streamAssistantReply(showPartial, signal);
      const formattedHTML = this.formatAssistantMessage(rawReply);
      const handlesList = this.extractProductHandles(rawReply, formattedHTML);

      let productHandle = null;
      if (this.productHandles.length > 0) {
        productHandle = this.productHandles[this.productHandles.length - 1];
      }

      this.finalizeAiResponse(productHandle, currentChatEl, formattedHTML);

      // 🛍️ Render suggested products if available
      if (handlesList.length > 0) {
        const handleAttr = handlesList.length > 1 ? handlesList.join(',') : handlesList[0];
        const productCard = this.productsContainer.querySelector(`.productCard[data-handle="${handleAttr}"]`);
        if (!productCard) {
          this.renderSuggestedProduct(currentChatEl, handlesList);
        }
      }
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      console.error('Error submitting message:', error);
      this.renderAssistantError(currentChatEl);
    } finally {
      this.activeStreamController = null;
    }
  }

  // Consumes the SSE stream from /conversations/ca/start/v3/stream, invoking
  // onPartial with the accumulated text after each delta. Resolves with the
  // canonical full message (done event), falling back to the accumulated deltas.
  async streamAssistantReply(onPartial, signal) {
    const resp = await fetch(`${this.apiBaseUrl}/conversations/ca/start/v3/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: this.email }),
      signal,
    });

    // Pre-stream failures (validation error, unknown email) come back as plain JSON with a 4xx status
    if (!resp.ok || !(resp.headers.get('content-type') || '').includes('text/event-stream') || !resp.body) {
      throw new Error(`Assistant stream unavailable (${resp.status})`);
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulated = '';
    let finalMessage = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';
      for (const frame of frames) {
        const line = frame.split('\n').find((l) => l.startsWith('data: '));
        if (!line) continue;
        let event;
        try {
          event = JSON.parse(line.slice(6));
        } catch {
          continue;
        }
        if (event.type === 'delta' && typeof event.text === 'string') {
          accumulated += event.text;
          onPartial(accumulated);
        } else if (event.type === 'done') {
          finalMessage = event.lastAssistantMessage ?? accumulated;
        } else if (event.type === 'error') {
          throw new Error(event.error || 'Assistant stream error');
        }
      }
    }

    const fullMessage = finalMessage ?? accumulated;
    if (!fullMessage) {
      throw new Error('Assistant stream returned no content');
    }
    return fullMessage;
  }

  formatAssistantMessage(responseHTML) {
    let formattedHTML = responseHTML;

    if (responseHTML.includes('[**') || responseHTML.includes('](https')) { //new format
      // Escape problematic ampersands first
      formattedHTML = formattedHTML.replace(/&(?!(?:[a-z]+|#\d+);)/gi, '&amp;');

      const normalizeName = (text) => text.replaceAll('*', '').trim().toLowerCase();
      const chipLink = (name, url) => {
        let href = url;
        try {
          href = new URL(url).href;
        } catch {
          // keep the raw url
        }
        return `<a class="product-link product-link--chip" href="${href}" target="_blank"><span class="scent-highlight" data-scent-family>${name}</span></a>`;
      };

      // Collect the product URL each [Name](url) link points to
      const linkUrlByName = {};
      for (const match of formattedHTML.matchAll(/\[(.*?)\]\((https?:\/\/[^\s)]+)\)/g)) {
        linkUrlByName[normalizeName(match[1])] = match[2];
      }

      const chippedNames = new Set();

      // [**Name**](url) links become a single linked chip
      formattedHTML = formattedHTML.replace(/\[\*\*(.+?)\*\*\]\((https?:\/\/[^\s)]+)\)/g, (match, boldText, url) => {
        const name = boldText.trim();
        chippedNames.add(normalizeName(name));
        return chipLink(name, url);
      });

      // Bold product names become a linked chip too; other bold text stays plain
      formattedHTML = formattedHTML.replace(/\*\*(.+?)\*\*/g, (match, boldText) => {
        const name = boldText.trim();
        const url = linkUrlByName[normalizeName(name)];
        if (!url) return name;
        chippedNames.add(normalizeName(name));
        return chipLink(name, url);
      });

      // Remaining links: the ones repeating a product already shown as a chip
      // are dropped together with the dash/colon separating them, so the name
      // isn't rendered twice; the rest become linked chips as well
      formattedHTML = formattedHTML.replace(/([ \t]*[—–:][ \t]*|[ \t]*)\[(.*?)\]\((https?:\/\/[^\s)]+)\)/g, (match, separator, linkText, url) => {
        const name = linkText.replaceAll('*', '').trim();
        if (chippedNames.has(normalizeName(name))) return '';
        return `${separator}${chipLink(name, url)}`;
      });

      // Drop list dashes left in front of a chip, emptied-out bullets and
      // parentheses, so no stray dashes remain
      formattedHTML = formattedHTML.replace(/(^|\n)[ \t]*-[ \t]+(?=<a|<span)/g, '$1');
      formattedHTML = formattedHTML.replace(/(^|\n)[ \t]*-[ \t]*(?=\n|$)/g, '$1');
      formattedHTML = formattedHTML.replace(/[ \t]*\(\s*\)/g, '');

      // Replace newlines with <br> tags
      formattedHTML = formattedHTML.replace(/\n\n/g, '<br><br>').replace(/\n/g, '<br>');
    } else {
      // Wrap **...** with <span class="scent-highlight"></span>
      formattedHTML = responseHTML.replace(/"([^"]{3,})"/g, '<span class="scent-highlight" data-scent-family>$1</span>').replace(/\*\*(.*?)\*\*/g, '<span class="scent-highlight" data-scent-family>$1</span>');

      // Replace [text](url) or Replace plain URLs (with or without protocol)
      let firstLinkHandled = false;

      formattedHTML = formattedHTML.replace(/\[(.*?)\]\((https?:\/\/[^\s)]+)\)/g, (match, linkText, url) => {
        try {
          const urlObj = new URL(url);
          const pathParts = urlObj.pathname.split('/').filter(Boolean);
          const rawHandle = pathParts.pop() || '';
          if (!firstLinkHandled) {
            this.productHandles.push(rawHandle);
            firstLinkHandled = true;
          }
          const finalLinkText = linkText;
          return `<a class="product-link" href="${urlObj.href}" target="_blank">${finalLinkText}</a>`;
        } catch {
          return match;
        }
      });

      // Replace newlines with <br> tags
      formattedHTML = formattedHTML.replace(/\n\n/g, '<br><br>').replace(/\n/g, '<br>');

      // Escape problematic ampersands last
      formattedHTML = formattedHTML.replace(/&(?!(?:[a-z]+|#\d+);)/gi, '&amp;');
    }

    return formattedHTML;
  }

  // Collects recommended product handles from the reply, deduped per product
  extractProductHandles(rawText, formattedHTML) {
    const handles = [];

    // Absolute product URLs kept in the rendered reply
    const urlRegex = /https:\/\/[^"?]*\/([^"?/]+)(?=[?" ])/g;
    for (const match of formattedHTML.matchAll(urlRegex)) {
      handles.push(match[1]);
    }

    // Relative /products/<handle> links from the raw markdown
    const pathRegex = /\/products\/([a-z-]+(?<!-))(?:-\d+)?/g;
    for (const match of rawText.matchAll(pathRegex)) {
      handles.push(match[1]);
    }

    const seen = new Set();
    const uniqueHandles = [];
    for (const handle of handles) {
      const normalized = handle.replace(/-\d+$/, '').replace(/-v2$/, '');
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      uniqueHandles.push(handle);
    }
    return uniqueHandles;
  }

  renderChatTemplate(userMessage) {
    const newChat = this.chatTemplate.content.cloneNode(true).firstElementChild;
    newChat.dataset.key = this.chatIndex++;
    this.toggleLoadingChatState(true);
    newChat.querySelector('.user-message').textContent = userMessage;
    this.chatbox.appendChild(newChat);
  }

  finalizeAiResponse(productHandle, chatElement, responseHTML) {
    if (productHandle) {
      chatElement.dataset.productHandle = productHandle;
    }

    const wrapper = chatElement.querySelector('.ai-message-wrapper');
    const loadingEl = wrapper.querySelector('.loading-element');
    if (loadingEl) loadingEl.remove();

    let aiMessageEl = wrapper.querySelector('.ai-message');
    if (!aiMessageEl) {
      aiMessageEl = document.createElement('div');
      aiMessageEl.classList.add('ai-message');
      wrapper.appendChild(aiMessageEl);
    }

    // Swap the streamed plain text for the rendered markdown
    aiMessageEl.innerHTML = responseHTML;

    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    this.toggleLoadingChatState(false);
    this.updateScentHighlightInChat(chatElement);
    this.persistTranscript();
  }

  renderAssistantError(chatElement) {
    if (chatElement) {
      chatElement.dataset.error = 'true';
      const wrapper = chatElement.querySelector('.ai-message-wrapper');
      const loadingEl = wrapper.querySelector('.loading-element');
      if (loadingEl) loadingEl.remove();

      let aiMessageEl = wrapper.querySelector('.ai-message');
      if (!aiMessageEl) {
        aiMessageEl = document.createElement('div');
        aiMessageEl.classList.add('ai-message');
        wrapper.appendChild(aiMessageEl);
      }
      aiMessageEl.textContent = window.assistantErrorText || 'Sorry, something went wrong. Please try again.';
    }
    this.toggleLoadingChatState(false);
  }

  toggleLoadingChatState(isLoading) {
    this.dossierPerfumeAssistant.classList.toggle('loading', isLoading);
    document.body.classList.toggle('loading-ai-message', isLoading);
    this.chatForm.querySelector('#query').readOnly = isLoading;
    this.chatForm.querySelector('button').disabled = isLoading;
  }

  // Suggested starter questions from the assistant API (already localized per
  // region). The locale-file chips rendered by Liquid stay as the fallback.
  async loadSuggestedQueries() {
    try {
      const response = await fetch(`${this.apiBaseUrl}/conversations/suggested-queries?region=ca`);
      if (!response.ok) return;
      const data = await response.json();
      if (!Array.isArray(data.suggestions) || data.suggestions.length === 0) return;
      this.renderSuggestionChips(data.suggestions);
    } catch (error) {
      // keep the locale-provided fallback chips
    }
  }

  renderSuggestionChips(suggestions) {
    this.perfumeSuggestions.forEach((container) => {
      // The search step is a fixed grid sized for 4 chips; the chat-step slideshow scrolls
      const isSearchStep = container.closest('.perfume-assistant__search') !== null;
      const chipTexts = isSearchStep ? suggestions.slice(0, 4) : suggestions;

      container.querySelectorAll('.perfume-assistant__suggestion:not(.perfume-assistant__suggestion-restart)').forEach((chip) => chip.remove());
      chipTexts.forEach((text) => {
        const chip = document.createElement('div');
        chip.classList.add('perfume-assistant__suggestion');
        chip.textContent = text;
        container.appendChild(chip);
      });
    });
  }

  // Keeps the transcript in sessionStorage so in-site navigation doesn't wipe
  // the chat (server-side conversation state survives regardless, keyed by email)
  persistTranscript() {
    try {
      const entries = Array.from(this.chatbox.querySelectorAll('.perfume-assistant__chat'))
        .filter((chatEl) => chatEl.dataset.error !== 'true')
        .map((chatEl) => ({
          user: chatEl.querySelector('.user-message')?.textContent || '',
          assistantHtml: chatEl.querySelector('.ai-message')?.innerHTML || '',
          handles: chatEl.dataset.handles ? JSON.parse(chatEl.dataset.handles) : [],
        }))
        .filter((entry) => entry.user && entry.assistantHtml);
      sessionStorage.setItem(this.transcriptStorageKey, JSON.stringify(entries));
    } catch (error) {
      // sessionStorage unavailable — the in-page chat still works
    }
  }

  restoreTranscript() {
    if (!this.email) return;

    let entries = [];
    try {
      entries = JSON.parse(sessionStorage.getItem(this.transcriptStorageKey)) || [];
    } catch (error) {
      return;
    }
    if (!Array.isArray(entries) || entries.length === 0) return;

    entries.forEach((entry) => {
      if (!entry || !entry.user || !entry.assistantHtml) return;
      const newChat = this.chatTemplate.content.cloneNode(true).firstElementChild;
      newChat.dataset.key = this.chatIndex++;
      newChat.querySelector('.user-message').textContent = entry.user;

      const wrapper = newChat.querySelector('.ai-message-wrapper');
      const loadingEl = wrapper.querySelector('.loading-element');
      if (loadingEl) loadingEl.remove();
      const aiMessageEl = document.createElement('div');
      aiMessageEl.classList.add('ai-message');
      aiMessageEl.innerHTML = entry.assistantHtml;
      wrapper.appendChild(aiMessageEl);

      this.chatbox.appendChild(newChat);

      if (Array.isArray(entry.handles) && entry.handles.length > 0) {
        newChat.dataset.handles = JSON.stringify(entry.handles);
        this.renderSuggestedProduct(newChat, entry.handles);
      }
    });

    if (this.chatIndex > 0) {
      this.updatePerfumeAssistantStep(3);
    }
  }

  clearTranscriptStorage() {
    try {
      sessionStorage.removeItem(this.transcriptStorageKey);
    } catch (error) {
      // nothing to clear
    }
  }

  renderSuggestedProduct(chatElement, productHandles) {
    // Fetch product details using the Storefront API
    let STOREFRONT_TOKEN = '086778bf7bffd172eeb44de0bbcec99c';

    const chatKey = chatElement.dataset.key;
    chatElement.dataset.handles = JSON.stringify(productHandles);

    productHandles = productHandles.map(str => str.replaceAll('-1', '').replaceAll('-v2', ''));

    let queryString = productHandles.map(h => `title:${h}`).join(' OR ');

    // remove specific handle text error
    queryString = queryString.replaceAll('-1', '').replaceAll('-v2', '')

    let query_aux = `
      query getByHandles($filter: String!) {
        products(first: 10, query: $filter ) {
          edges {
            node {
              id
              publishedAt
              availableForSale
              title
              handle
              tags
              productType
              vendor
              totalInventory
              priceRange {
                maxVariantPrice {
                  amount
                  currencyCode
                }
              }
              media(first: 1) {
                edges {
                  node {
                    ... on MediaImage {
                      image {
                        url
                        altText
                      }
                    }
                  }
                }
              }
              variants(first: 1) {
                edges {
                  node {
                    id
                    title
                    availableForSale
                    price {
                      amount
                      currencyCode
                    }
                  }
                }
              }
              metafield(namespace: "custom", key: "scent_family") {
                value
                type
              }
            }
          }
        }
      }`

    fetch('/api/2025-04/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({
        query: query_aux,
        variables: {
          filter: queryString
        }
      })
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.status}`);
        }
        return response.json();
      })
      .then(({ data }) => {

        this.viewKey = chatKey;
        const currentChatEl = chatElement;

        //helper functions

        function parseProductData(product) {
          const variant = product.variants?.edges?.[0]?.node || {};
          const variantId = variant.id.split('/').pop() || '';
          const media = product.media?.edges?.[0]?.node?.image || {};
          const maxPrice = product.priceRange?.maxVariantPrice?.amount;

          return {
            id: product.id || '',
            variantId: variantId || '',
            title: product.title || '',
            handle: product.handle || '',
            url: `/products/${product.handle || ''}`,
            type: product.productType || '',
            vendor: product.vendor || '',
            price: parseFloat(maxPrice || '0').toString(),
            imageUrl: media.url || '',
            imageAlt: media.altText || '',
            scentFamily: product.metafield?.value || '',
            availableForSale: product.availableForSale ?? false,
            variantAvailable: variant.availableForSale ?? false
          };
        }

        // Format Product Vendor
        function formatVendorText(text) {

          const regex = new RegExp(`^${inspiredByText}\\s*`, 'i');
          const cleanedText = text.replace(regex, '').trim();

          const parts = cleanedText.split(" de");
          let formattedVendor = '';

          if (parts.length >= 3) {
            formattedVendor = `${parts[0]} de ${parts[1]} de <strong>${parts[2]}</strong>`;
          } else if (parts.length === 2) {
            formattedVendor = `${parts[0]} de <strong>${parts[1]}</strong>`;
          } else {
            formattedVendor = `<strong>${cleanedText}</strong>`;
          }

          return `${inspiredByText}<br>${formattedVendor}`;
        }

        // Format Product Title
        function formatTitleText(text) {
          const parts = text.split(' ');
          if (parts.length > 1) {
            return parts[0] + ' <br> ' + parts.slice(1).join(' ');
          }
          return text;
        }

        //END helper functions

        if (data.products.edges.length > 0) {
          let products = data.products.edges

          // create slider for it

          let sliderEl = `
          <div class='carousel__recomended-products-wrapper ${products.length == 1 ? 'just-one' : ''}' data-key="${chatKey}">
            <div class="blaze-slider carousel__recomended-products" >
              <div class="blaze-container">
                <div class="blaze-track-container">
                  <div class="blaze-track">
                  </div>
                </div>
                <div class='nav_arrow_wrapper'>
                  <button class="blaze-prev"><img src="https://cdn.shopify.com/s/files/1/0625/4631/3452/files/arrow_black_left_v2.svg?v=1770300274" loading="lazy"></button>
                  <button class="blaze-next"><img src="https://cdn.shopify.com/s/files/1/0625/4631/3452/files/arrow_black_left_v2.svg?v=1770300274" loading="lazy"></button>
                </div>
                <div class="pagination-wrapper">
                  <div class="blaze-pagination"></div>
                </div>
              </div>
            </div>
          </div>

          `


          if (window.innerWidth < 768) {
            const aiRecomandedProductsWrapper = document.createElement('div');
            aiRecomandedProductsWrapper.classList.add('products-recommended', 'mobile-v', 'desktop-hidden');
            currentChatEl.appendChild(aiRecomandedProductsWrapper);

            currentChatEl.querySelector('.products-recommended.mobile-v').insertAdjacentHTML('beforeend', sliderEl);
          } else {

            this.productsContainer.insertAdjacentHTML('beforeend', sliderEl);
          }
          // END create slider for it

          // sort product based on the AI response product order
          const sortedProducts = productHandles.map(t => {
            return products.find(item => item.node.handle === t);
          }).filter(item => item !== undefined);


          sortedProducts.forEach((element, index) => {
            let product = element.node;

            //create product card
            const productData = parseProductData(product);
            const formattedTitle = formatTitleText(productData.title);
            const formattedVendor = formatVendorText(productData.vendor);

            let productActionHTML = `
            <product-form>
                <form method="post" action="/cart/add" id="quick-add-template--product-grid" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
                  <input type="hidden" name="form_type" value="product">
                  <input type="hidden" name="utf8" value="✓">
                  <input type="hidden" name="id" value="${productData.variantId}">
                  <input type="hidden" name="properties[_aine]" value="true">
                  <button type="submit" name="add" class="quick-add__submit button button--full-width button--primary" aria-haspopup="dialog" aria-live="polite" data-sold-out-message="true">
                    <span>${productAddToCartText}</span>
                    <div class="loading-overlay__spinner hidden">
                      <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                      </svg>
                    </div>
                  </button>
                  <input type="hidden" name="product-id" value="${productData.variantId}">
                  <input type="hidden" name="section-id" value="product-grid">
                </form>
              </product-form>
              `;

            if (!productData.availableForSale || !productData.variantAvailable) {
              productActionHTML = `<product-form><ds-button><button class="quick-add__submit button button--full-width button--primary js-back-in-stock" data-modal-target="#back-in-stock-modal" data-variant-id="${productData.variantId}" data-product-title="${productData.title}" data-product-vendor="${productData.vendor}"><span>${productNotifyMeText}</span><div class="loading-overlay__spinner hidden"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div></button></ds-button></product-form>`;
            }

            let productCardHtml = `
            <div id="productCard__${productData.handle}" class="productCard active" data-id="${productData.id}" data-variant-id="${productData.variantId}" data-handle="${productData.handle}" data-title="${productData.title}" data-type="${productData.type}" data-scent-family="${productData.scentFamily}" data-index=${index}>
              <div class="productCard__container">
                <div class="productCard__image">
                  <a href="${productData.url}" class="productCard__image-link" target="_blank">
                    <img src="${productData.imageUrl}" alt="${productData.imageAlt}" class="productCard__image-img" width="326" height="326">
                  </a>
                </div>
                <div class="productCard__content">
                  <a href="${productData.url}" class="productCard__link" target="_blank">
                    <h3 class="productCard__title">${formattedTitle}</h3>
                    <div class="productCard__price">
                      <span class="price">${productCurrency}${productData.price}</span>
                    </div>
                    <div class="productCard__vendor">${formattedVendor}</div>
                  </a>
                  <div class="productCard__actions">${productActionHTML}</div>
                </div>
              </div>
            </div>
            `;
            //END create product card

            // add elements to slider

            if (window.innerWidth < 768) {
              currentChatEl.querySelector('.products-recommended.mobile-v').querySelector(`.carousel__recomended-products-wrapper[data-key='${chatKey}'] .blaze-track`).insertAdjacentHTML('beforeend', productCardHtml);
            } else {
              this.productsContainer.querySelector(`.carousel__recomended-products-wrapper[data-key='${chatKey}'] .blaze-track`).insertAdjacentHTML('beforeend', productCardHtml);
            }
          })

          //init slider

          let productCarouselEL = this.productsContainer.querySelector(`.carousel__recomended-products-wrapper[data-key='${chatKey}'] .carousel__recomended-products`);
          if (window.innerWidth < 768) {
            productCarouselEL = currentChatEl.querySelector('.products-recommended.mobile-v').querySelector(`.carousel__recomended-products-wrapper[data-key='${chatKey}'] .carousel__recomended-products`);
          }

          this.updateScentFamilyBodyClass();
          productCarouselEL.querySelector(`.productCard`).classList.add('currentActive')

          if (productCarouselEL && sortedProducts.length > 1) {
            const options = {
              all: {
                slidesToShow: 1,
                slidesToScroll: 1,
                loop: false,
                // transitionDuration: 250,
                draggable: false,
                slideGap: '1.6rem',
              },
              '(max-width: 768px)': {
                slidesToShow: 1,
                slidesToScroll: 1,
                slideGap: '.8rem',
                draggable: true,
                loop: false,
              },
            }

            let productCarousel = new BlazeSlider(productCarouselEL, options);
            let cards = productCarouselEL.querySelectorAll(`.productCard`);
            cards[0].classList.add('currentActive'); //make first card in slider the current active
            const unsubscribe = productCarousel.onSlide(
              (pageIndex) => {
                cards.forEach(card => {
                  if (card.dataset.index == pageIndex) {
                    card.classList.add('currentActive')
                  }
                  else {
                    card.classList.remove('currentActive')
                  }
                })
                this.updateScentFamilyBodyClass();// update scent family effect on slide change
              }
            );
          }
          // END init slider

          // add scent family to the chat product titles
          let scentsFamily = products.map(product => ({ name: product.node.title, scentFamily: product.node?.metafield?.value || '' }))
          this.productHandlesWithScentFamily = [...scentsFamily]
          // END add scent family to the chat product titles

          this.updateScentHighlightInChat(currentChatEl);

        } else {
          console.error('Products not found');
          return;
        }


        this.updateCurrentRecomandations();
        this.updateScentFamilyBodyClass();
        this.persistTranscript();

      })
      .catch(error => {
        console.error('Fetch error:', error);
      });
  }

  removeScentFamilyBodyClass() {
    this.dossierPerfumeAssistant.classList.forEach(className => {
      if (className.startsWith("scent-family__")) {
        this.dossierPerfumeAssistant.classList.remove(className);
      }
    });
  }

  updateScentFamilyBodyClass() {
    let currentActiveElement = this.dossierPerfumeAssistant.querySelector('.perfume-assistant__ai-results .carousel__recomended-products-wrapper.active .productCard.currentActive');

    if (window.innerWidth < 768) {
      currentActiveElement = this.dossierPerfumeAssistant.querySelector(`.perfume-assistant__chat .products-recommended.mobile-v .carousel__recomended-products-wrapper.active .productCard.currentActive`)
    }

    if (currentActiveElement) {
      let scentFamily = currentActiveElement.dataset?.scentFamily.toLowerCase();
      if (scentFamily) {
        this.dossierPerfumeAssistant.classList.forEach(className => {
          if (className.startsWith('scent-family__')) {
            this.dossierPerfumeAssistant.classList.remove(className);
          }
        });

        this.dossierPerfumeAssistant.classList.add(`scent-family__${scentFamily}`);
      }
    }
  }


  updateCurrentRecomandations(specificKey) {
    //changes what products recomandation slider is shown based on chat in focus

    let key;
    if (specificKey != undefined) {
      key = specificKey
    } else {
      key = this.viewKey
    }

    let recomandationsList = this.dossierPerfumeAssistant.querySelectorAll(`.perfume-assistant__products .carousel__recomended-products-wrapper`)
    if (window.innerWidth < 768) {
      recomandationsList = this.dossierPerfumeAssistant.querySelectorAll('.perfume-assistant__chatbox .products-recommended.mobile-v .carousel__recomended-products-wrapper')
    }
    recomandationsList.forEach(element => {
      if (element.dataset.key == key) {
        element.classList.add('active');
      } else {
        element.classList.remove('active');
      }
    })

    this.updateScentFamilyBodyClass();
  }

  initSuggestionsSlideshow() {
    if (!this.suggestionsSlideshowContent || !this.suggestionsSlideshowNav) return;

    const scrollContainer = this.suggestionsSlideshowContent;
    const scrollButton = this.suggestionsSlideshowNav.querySelector('.slider-next');

    if (!scrollButton) return;

    scrollButton.addEventListener('click', () => {
      const scrollAmount = scrollContainer.clientWidth;
      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const currentScrollLeft = scrollContainer.scrollLeft;

      if (currentScrollLeft >= maxScrollLeft - 1) {
        scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        const targetScrollLeft = Math.min(currentScrollLeft + scrollAmount, maxScrollLeft);
        scrollContainer.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
      }
    });
  }

  updateContentOnScroll() {
    window.addEventListener('scroll', () => {

      if (document.body.classList.contains('loading-ai-message')) {
        return
      }

      const triggerLine = window.innerHeight - (window.innerHeight * 0.45);
      let listItems = document.querySelectorAll('.perfume-assistant__chat');
      let closestElement = null;
      let minDistance = Infinity;

      listItems.forEach(item => {
        const rect = item.getBoundingClientRect();

        const itemMidpoint = rect.top + (rect.height / 2);
        const distance = Math.abs(triggerLine - itemMidpoint);

        if (distance < minDistance) {
          minDistance = distance;
          closestElement = item;
        }
      });

      if (closestElement) {
        let elementKey = closestElement.dataset.key;
        this.updateCurrentRecomandations(elementKey);
      }
    });
  }

  navigateBack() {
    if (!this.prevStepButton) return;
    this.prevStepButton.addEventListener('click', (event) => {
      event.preventDefault();
      history.back();
    });
  }

  handleSuggestionSelection() {
    // Delegated so chips replaced by loadSuggestedQueries keep working
    this.perfumeSuggestions.forEach(suggestionsContainer => {
      suggestionsContainer.addEventListener('click', (e) => {
        const suggestion = e.target.closest('.perfume-assistant__suggestion');
        if (!suggestion || !suggestionsContainer.contains(suggestion)) return;

        if (e.target.closest('.perfume-assistant__suggestion-restart')) {
          this.restartConversation();
          return;
        }

        if (document.body.classList.contains('loading-ai-message')) return;

        const suggestionText = suggestion.textContent.trim();
        const stepForm = suggestion.closest(`.${this.perfumeAssistantClass}`).querySelector(`.${this.perfumeAssistantFormClass}`);
        if (!stepForm) return;
        const input = stepForm.querySelector('input[type="text"]');
        if (!input) return;
        input.value = suggestionText.replace(/(\S)/, (m) => m.toUpperCase());
        stepForm.dispatchEvent(new Event('submit', { bubbles: true }));

        const currentStep = parseInt(document.body.dataset.perfumeAssistantStep) || 1;
        if (currentStep === 3) {
          window.scrollTo(0, document.body.scrollHeight);
        }
      });
    });
  }

  // Clears the local transcript and returns to the first screen. The
  // server-side conversation for this email continues where it left off.
  restartConversation() {
    if (this.activeStreamController) {
      this.activeStreamController.abort();
      this.activeStreamController = null;
    }

    this.clearTranscriptStorage();
    this.productHandles = [];
    this.productHandlesWithScentFamily = [];
    this.pendingMessage = null;

    this.updatePerfumeAssistantStep(2);
    if (this.aiResults.querySelector('.perfume-assistant__products')) {
      this.aiResults.querySelector('.perfume-assistant__products').innerHTML = ``;
    }
    this.aiResults.querySelector('.perfume-assistant__chatbox').innerHTML = ``;
    this.dossierPerfumeAssistant.classList.forEach(className => {
      if (className.startsWith('scent-family__')) {
        this.dossierPerfumeAssistant.classList.remove(className);
      }
    });

    //remove loading classes
    this.toggleLoadingChatState(false);
    document.querySelector('#chat__form').classList.remove('error');
  }

  toggleRecommendedProducts() {
    document.addEventListener('click', (e) => {
      const recommendedBtn = e.target.closest(`.${this.buttonToggleRecommendedClass}`);
      if (recommendedBtn && this.chatStep) {
        this.productsRecommended.classList.toggle('active');
        this.chatStep.classList.toggle('show-recommended');
      }
    });
  }

  updateScentHighlightInChat(chatElement) {
    const currentChat = chatElement || this.dossierPerfumeAssistant.querySelector(`.perfume-assistant__chat[data-key='${this.chatIndex - 1}']`);
    if (!currentChat) return;
    let scentHighlightsEls = currentChat.querySelectorAll('.scent-highlight');

    if (this.productHandlesWithScentFamily.length > 0) {

      this.productHandlesWithScentFamily.forEach(el => {
        scentHighlightsEls.forEach(subEl => {
          if (subEl.innerText == el.name) {
            subEl.dataset.scentFamily = el.scentFamily.toLowerCase();
          }
        })
      })
    }
  }

  initMobileUpdate() {
    if (window.innerWidth <= 900) {
      this.introForm.querySelector('input[type="email"]').placeholder = emailInputPlaceholderMobile;
    }
  }

  closeAineePopup() {
    this.aineePopup.addEventListener('click', (event) => {
      const modalContent = this.aineePopup.querySelector('.ds-modal__content');
      const insideContent = modalContent.contains(event.target);
      const showPopup = this.aineePopup.classList.contains('show');

      if (showPopup && !insideContent) {
        this.aineePopup.classList.remove('show');
      }
    });
  }
}

const dossierPerfumeAssistant = new DossierPerfumeAssistant();
dossierPerfumeAssistant.init();
